import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Col, Row, Typography } from 'antd';
import { capitalCase } from 'change-case';
import { TbCalendarDue, TbCalendarEvent, TbClock, TbCubePlus, TbRefresh } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import EnrichedPill from '@/shared/components/enriched-pills/EnrichedPill';
import EmptyView from '@/shared/components/layouts/views/EmptySection';
import { EnrichedWorkspaceResourcePopover } from '@/shared/components/popovers';
import CompactCircularProgress from '@/shared/components/progress/CompactCircularProgress';
import PlatformProgressBar from '@/shared/components/progress/PlatformProgressBar';
import SmallTag from '@/shared/components/tags/SmallTag';
import TaskParticipantAvatarGroup from '@/shared/components/tasks/TaskParticipantAvatarGroup';
import SmallTooltip from '@/shared/components/tooltips/SmallTooltip';
import { MiddleDot } from '@/shared/components/typography';
import WorkspaceResourceAvatar from '@/shared/components/workspace-resources/WorkspaceResourceAvatar';
import type { TaskAssignee } from '@/types';
import { formatDate } from '@/utilities';

import LifecycleStageCollapsible from '../LifecycleStageCollapsible';
import LifecycleIcon from '../LifecycleIcon';
import { LifecycleMemberSummaryDropdown, LifecycleTaskSummaryDropdown } from '../LifecycleSummaryDropdowns';
import {
    isLifecycleTaskCompleted,
    lifecycleDateDisplay,
    lifecycleProgressSummary,
    lifecycleRemainingLabel,
    lifecycleStatusName,
    lifecycleTasks,
} from '../progress';
import type { LifecycleDateTone } from '../progress';
import type { LifecyclePreview } from '../types';

interface LifecyclePreviewPanelProps {
    lifecycle: LifecyclePreview;
    showFooter?: boolean;
}

const uniqueParticipants = (participants: TaskAssignee[]) =>
    participants.filter(
        (participant, index, collection) =>
            collection.findIndex((candidate) => candidate.member.id === participant.member.id) === index,
    );

const relativeDate = (value?: Date | string | null) =>
    value && !Number.isNaN(new Date(value).valueOf()) ? formatDate(new Date(value), 'mini') : undefined;

export const LifecyclePreviewFooter: FC<Pick<LifecyclePreviewPanelProps, 'lifecycle'>> = ({ lifecycle }) => {
    const { token } = useAntDesignTheme();
    const createdAt = relativeDate(lifecycle.createdAt);
    const updatedAt = relativeDate(lifecycle.updatedAt);

    if (!createdAt && !updatedAt) {
        return null;
    }

    return (
        <Row
            align='middle'
            data-lifecycle-preview-footer='true'
            justify='space-between'
            style={{
                background: token.colorBgContainer,
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                minHeight: '2.25rem',
                padding: '0.5rem 0.75rem',
            }}
            wrap={false}
        >
            <Col flex='none'>
                {createdAt ? (
                    <small style={{ color: token.colorTextTertiary }}>
                        <TbCubePlus
                            fill={token.colorBorder}
                            stroke={token.colorTextTertiary}
                            style={{ marginRight: '0.25rem', verticalAlign: '-1.5px' }}
                        />
                        Created {createdAt}
                    </small>
                ) : null}
            </Col>
            <Col flex='none'>
                {updatedAt ? (
                    <small style={{ color: token.colorTextTertiary }}>
                        <TbRefresh
                            fill={token.colorBorder}
                            stroke={token.colorTextTertiary}
                            style={{ marginRight: '0.25rem', verticalAlign: '-1.5px' }}
                        />
                        Last updated {updatedAt}
                    </small>
                ) : null}
            </Col>
        </Row>
    );
};

const LifecyclePreviewPanel: FC<LifecyclePreviewPanelProps> = ({ lifecycle, showFooter = true }) => {
    const { token } = useAntDesignTheme();
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);
    const progress = lifecycleProgressSummary(lifecycle);
    const tasks = useMemo(() => lifecycleTasks(lifecycle), [lifecycle]);
    const completedTasks = useMemo(() => tasks.filter(isLifecycleTaskCompleted), [tasks]);
    const statusName = capitalCase(lifecycleStatusName(lifecycle));
    const statusColor =
        lifecycle.status?.color ??
        {
            'awaiting review': token.colorWarning,
            cancelled: token.colorError,
            complete: token.colorSuccess,
            draft: token.colorTextTertiary,
            'in progress': token.colorInfo,
            planned: token.colorPrimary,
        }[statusName.toLowerCase()] ??
        token.colorPrimary;
    const startDate = lifecycleDateDisplay(lifecycle.startAt, 'start', statusName);
    const endDate = lifecycleDateDisplay(lifecycle.endAt, 'end', statusName);
    const remainingLabel = lifecycleRemainingLabel(lifecycle.endAt, statusName);
    const assignees = useMemo(
        () =>
            uniqueParticipants(
                lifecycle.assignees ??
                    lifecycle.collaborators ??
                    lifecycle.stages.flatMap((stage) => stage.tasks.flatMap((task) => task.assignees ?? [])),
            ),
        [lifecycle],
    );
    const reviewers = useMemo(
        () =>
            uniqueParticipants(
                lifecycle.reviewers ??
                    lifecycle.stages.flatMap((stage) => stage.tasks.flatMap((task) => task.reviewers ?? [])),
            ),
        [lifecycle],
    );
    const participants = useMemo(() => uniqueParticipants([...assignees, ...reviewers]), [assignees, reviewers]);
    const dateToneColor: Record<LifecycleDateTone, string> = {
        danger: token.colorError,
        default: token.colorTextTertiary,
        warning: token.colorWarning,
    };

    return (
        <div data-lifecycle-preview-id={lifecycle.id}>
            <div style={{ padding: '1rem' }}>
                <Row align='top' gutter={[8, 0]} wrap={false}>
                    <Col flex='none' style={{ alignSelf: 'center' }}>
                        <WorkspaceResourceAvatar resource='lifecycle' size={42} />
                    </Col>
                    <Col flex='auto' style={{ minWidth: 0 }}>
                        <Typography.Text ellipsis strong style={{ display: 'block' }}>
                            {lifecycle.label}
                        </Typography.Text>
                        {lifecycle.associatedResource ? (
                            <div style={{ marginTop: '0.125rem' }}>
                                <EnrichedWorkspaceResourcePopover
                                    placement='bottomLeft'
                                    reference={lifecycle.associatedResource}
                                >
                                    <EnrichedPill href={lifecycle.associatedResource.href} style={{ margin: 0 }}>
                                        <WorkspaceResourceAvatar
                                            resource={lifecycle.associatedResource.resourceType}
                                            size={16}
                                            website={lifecycle.associatedResource.website}
                                        />
                                        <span
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {lifecycle.associatedResource.label}
                                        </span>
                                    </EnrichedPill>
                                </EnrichedWorkspaceResourcePopover>
                            </div>
                        ) : null}
                    </Col>
                    <Col flex='none'>
                        <SmallTag
                            accentColor={statusColor}
                            compact
                            icon={
                                <CompactCircularProgress
                                    percent={progress.percent}
                                    size={12}
                                    strokeColor={statusColor}
                                />
                            }
                            message={<span style={{ marginLeft: '0.25rem' }}>{statusName}</span>}
                            styles={{ marginLeft: 0 }}
                        />
                    </Col>
                </Row>

                {lifecycle.description ? (
                    <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, marginTop: '0.75rem' }}>
                        <Typography.Paragraph
                            ellipsis={{
                                expandable: 'collapsible',
                                expanded: descriptionExpanded,
                                rows: 2,
                                symbol: descriptionExpanded ? 'Show less' : 'Show more',
                                onExpand: (_event, info) => setDescriptionExpanded(info.expanded),
                            }}
                            style={{
                                color: token.colorTextTertiary,
                                margin: '0.75rem 0 0',
                                maxHeight: descriptionExpanded ? '5rem' : undefined,
                                overflowY: descriptionExpanded ? 'auto' : undefined,
                            }}
                        >
                            {lifecycle.description}
                        </Typography.Paragraph>
                    </div>
                ) : null}

                {startDate || endDate ? (
                    <Row align='middle' justify='space-between' style={{ marginTop: '0.875rem' }} wrap={false}>
                        <Col flex='none'>
                            {startDate ? (
                                <small
                                    style={{
                                        alignItems: 'center',
                                        color: dateToneColor[startDate.tone],
                                        display: 'inline-flex',
                                        gap: '0.25rem',
                                    }}
                                >
                                    <TbCalendarEvent aria-hidden size='0.875rem' /> {startDate.label}
                                </small>
                            ) : null}
                        </Col>
                        <Col flex='none'>
                            {endDate ? (
                                <small
                                    style={{
                                        alignItems: 'center',
                                        color: dateToneColor[endDate.tone],
                                        display: 'inline-flex',
                                        gap: '0.25rem',
                                    }}
                                >
                                    <TbCalendarDue aria-hidden size='0.875rem' /> {endDate.label}
                                </small>
                            ) : null}
                        </Col>
                    </Row>
                ) : null}
                <PlatformProgressBar
                    ariaLabel={`${lifecycle.label} progress: ${progress.percent}%`}
                    percent={progress.percent}
                    style={{ margin: '0.125rem 0 0' }}
                />

                <Row align='middle' justify='space-between' style={{ marginTop: '0.5rem' }} wrap={false}>
                    <Col flex='auto'>
                        <small style={{ color: token.colorTextTertiary }}>
                            <LifecycleTaskSummaryDropdown tasks={tasks}>
                                <span>
                                    {progress.totalTasks} {progress.totalTasks === 1 ? 'Task' : 'Tasks'}
                                </span>
                            </LifecycleTaskSummaryDropdown>
                            <MiddleDot horizontalMargin='0.375rem' />
                            <LifecycleTaskSummaryDropdown completed tasks={completedTasks}>
                                <span>{progress.completedTasks} Completed</span>
                            </LifecycleTaskSummaryDropdown>
                            <span> ({progress.percent}%)</span>
                        </small>
                    </Col>
                    {remainingLabel && endDate ? (
                        <Col flex='none'>
                            <SmallTooltip placement='topRight' showInfo={false} title={endDate.exactDateTime}>
                                <small
                                    style={{
                                        alignItems: 'center',
                                        color: dateToneColor[endDate.tone],
                                        cursor: 'help',
                                        display: 'inline-flex',
                                        gap: '0.25rem',
                                    }}
                                >
                                    <TbClock aria-hidden size='0.875rem' />
                                    {remainingLabel}
                                </small>
                            </SmallTooltip>
                        </Col>
                    ) : null}
                </Row>
            </div>

            <div
                style={{
                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                    display: 'grid',
                    gap: '0.5rem',
                    padding: '0.75rem',
                }}
            >
                {assignees.length || reviewers.length ? (
                    <Row
                        align='middle'
                        justify='space-between'
                        style={{ minHeight: '1.5rem', padding: '0 0.25rem 0.25rem' }}
                        wrap={false}
                    >
                        <Col flex='auto'>
                            <small style={{ color: token.colorTextTertiary }}>
                                <LifecycleMemberSummaryDropdown participants={assignees} variant='assignees'>
                                    <span>{assignees.length} Assignees</span>
                                </LifecycleMemberSummaryDropdown>
                                <MiddleDot horizontalMargin='0.375rem' />
                                <LifecycleMemberSummaryDropdown participants={reviewers} variant='reviewers'>
                                    <span>{reviewers.length} Reviewers</span>
                                </LifecycleMemberSummaryDropdown>
                            </small>
                        </Col>
                        <Col flex='none'>
                            <TaskParticipantAvatarGroup maxCount={5} participants={participants} size={18} />
                        </Col>
                    </Row>
                ) : null}
                {lifecycle.stages.length ? (
                    lifecycle.stages.map((stage) => <LifecycleStageCollapsible key={stage.id} stage={stage} />)
                ) : (
                    <EmptyView
                        description='Lifecycle stages and their tasks will appear here when they are configured.'
                        height='10rem'
                        icon={<LifecycleIcon size='1rem' />}
                        label='No Lifecycle Stages'
                    />
                )}
            </div>

            {showFooter ? <LifecyclePreviewFooter lifecycle={lifecycle} /> : null}
        </div>
    );
};

export default LifecyclePreviewPanel;
